import { Close, Search } from '@atom-learning/icons'
import * as React from 'react'

import { ActionIcon } from '~/components/action-icon/ActionIcon'
import { Icon } from '~/components/icon/Icon'
import { Input, InputBackground, InputText } from '~/components/input/Input'
import { styled } from '~/styled'
import { cn } from '~/styled'
import { useCallbackRef } from '~/utilities/hooks/useCallbackRef'
import { getFieldIconSize } from '~/utilities/style/get-icon-size'

export type SearchInputProps = React.ComponentProps<typeof Input> & {
  size?: 'sm' | 'md' | 'lg'
  className?: string
  value?: string
  defaultValue?: string
  onValueChange?: (newValue: string) => void
  clearText?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
}

enum INPUT_ICON {
  SEARCH = 'SEARCH',
  CLEAR = 'CLEAR'
}

const StyledIcon = styled(Icon, {
  base: ['text-grey-700', 'absolute', 'pointer-events-none'],
  variants: {
    size: {
      sm: ['right-2', 'size-4'],
      md: ['right-2.5', 'size-5'],
      lg: ['right-2.5', 'size-5']
    }
  }
})

export const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      size = 'md',
      appearance = 'standard',
      theme,
      className,
      value,
      defaultValue = '',
      onValueChange,
      clearText = 'Clear',
      onChange,
      ...remainingProps
    },
    ref
  ) => {
    const [inputElRef, setInputElRef] = useCallbackRef()
    const [innerValue, setInnerValue] = React.useState(defaultValue)
    const [activeIcon, setActiveIcon] = React.useState<INPUT_ICON>(
      defaultValue ? INPUT_ICON.CLEAR : INPUT_ICON.SEARCH
    )
    React.useEffect(() => {
      if (typeof value === 'undefined') return
      setInnerValue(value)
      setActiveIcon(value ? INPUT_ICON.CLEAR : INPUT_ICON.SEARCH)
    }, [value])

    const iconSize = React.useMemo(() => getFieldIconSize(size), [size])

    React.useImperativeHandle(ref, () => inputElRef.current as HTMLInputElement)

    const handleClear = () => {
      const inputEl = inputElRef.current
      if (!inputEl) return

      // https://stackoverflow.com/a/46012210
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value'
      )?.set
      nativeInputValueSetter?.call?.(inputEl, '')
      const ev2 = new Event('input', {
        bubbles: true
      })
      inputEl.dispatchEvent(ev2)
      inputEl.focus()
      onValueChange?.('')
    }

    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(event)

      const newValue = event.target.value
      setInnerValue(newValue)
      onValueChange?.(newValue)
      setActiveIcon(newValue ? INPUT_ICON.CLEAR : INPUT_ICON.SEARCH)
    }

    const getIcon = () => {
      if (activeIcon === INPUT_ICON.SEARCH)
        return (
          <StyledIcon
            is={Search}
            size={size}
            className={cn(
              size == 'sm' ? 'size-4' : 'size-5',
              'top-1/2',
              '-translate-y-1/2'
            )}
          />
        )

      return (
        <ActionIcon
          label={clearText}
          theme="neutral"
          size={iconSize}
          onClick={handleClear}
          className="absolute top-1/2 right-1 -translate-y-1/2"
        >
          <Icon is={Close} />
        </ActionIcon>
      )
    }

    return (
      <InputBackground
        size={size}
        appearance={appearance}
        theme={theme}
        className={cn('relative', 'w-auto', className)}
      >
        <InputText
          ref={setInputElRef}
          size={size}
          type="search"
          {...remainingProps}
          value={innerValue}
          onChange={handleOnChange}
          className={cn(
            size === 'sm' ? 'pr-8' : 'pr-10',
            '[&::-webkit-search-decoration]:appearance-none',
            '[&::-webkit-search-cancel-button]:appearance-none',
            '[&::-webkit-search-results-button]:appearance-none'
          )}
        />
        {getIcon()}
      </InputBackground>
    )
  }
)

SearchInput.displayName = 'SearchInput'
